/**
 * Collapsible Breadcrumb
 * ========================================================================
 * Collapses middle crumbs into a "…" dropdown when the trail doesn't fit
 * on one line, the way tabs-collapse.js folds tabs into "More".
 *
 * Usage — add data-breadcrumb-collapse to any .breadcrumb:
 *
 *   <nav class="breadcrumb" data-breadcrumb-collapse>
 *       <a href="/acqs/">Acquisitions</a>
 *       <span class="breadcrumb__sep">/</span>
 *       <a href="/acqs/team/">Team resources</a>
 *       <span class="breadcrumb__sep">/</span>
 *       <span>Seller marketing analysis</span>
 *   </nav>
 *
 * The first crumb and the current page always stay visible. Middle crumbs
 * collapse left to right, so the nearest parent is the last to go. If even
 * "first / … / current" doesn't fit, the current page truncates with an
 * ellipsis (CSS). Until this script runs the trail wraps like any breadcrumb.
 *
 * Re-runs when the breadcrumb's width changes (ResizeObserver), which also
 * covers one that starts hidden in a sheet or tab, once web fonts load, and
 * after HTMX swaps. While the "…" menu is open it waits and re-runs when the
 * menu closes. Integrates with the existing dropdown component (dropdowns.js).
 */

(function () {
    var ACTIVE_CLASS = 'breadcrumb--collapse';
    var OVERFLOW_CLASS = 'breadcrumb-overflow';
    var SEP_CLASS = 'breadcrumb__sep';
    var SELECTOR = '.breadcrumb[data-breadcrumb-collapse]';

    var lastWidth = new WeakMap();

    /**
     * Build or return the "…" dropdown and the separator that follows it.
     * Both sit right after the first crumb's separator.
     */
    function getOrCreateOverflow(nav, firstSep) {
        var existing = nav.querySelector(':scope > .' + OVERFLOW_CLASS);
        if (existing) return existing;

        var wrapper = document.createElement('div');
        wrapper.className = 'dropdown ' + OVERFLOW_CLASS;

        var trigger = document.createElement('button');
        trigger.className = 'breadcrumb-overflow__trigger';
        trigger.setAttribute('data-dropdown', '');
        trigger.setAttribute('data-dropdown-portal', '');
        trigger.setAttribute('type', 'button');
        trigger.setAttribute('aria-label', 'Show hidden pages');
        trigger.textContent = '…';

        var menu = document.createElement('div');
        menu.className = 'dropdown-menu';

        wrapper.appendChild(trigger);
        wrapper.appendChild(menu);

        // Kept as a reference: while open, dropdowns.js portals the menu to
        // <body>, so a querySelector from the wrapper can't find it
        wrapper._menu = menu;

        // Its own separator, cloned so it matches whatever the template uses
        wrapper._sep = firstSep.cloneNode(true);

        // dropdowns.js has no close event; watch the menu's .show instead so
        // a resize that happened while it was open is applied on close
        new MutationObserver(function () {
            if (!menu.classList.contains('show') && nav._collapsePending) {
                nav._collapsePending = false;
                updateCollapse(nav);
            }
        }).observe(menu, { attributes: true, attributeFilter: ['class'] });

        firstSep.after(wrapper, wrapper._sep);
        return wrapper;
    }

    function setShown(el, shown) {
        el.style.display = shown ? '' : 'none';
    }

    /**
     * True when the visible children fit on one line. The current page is
     * allowed to shrink in CSS, so it's measured by its content width.
     * Fractional widths are summed unrounded, with 1px to spare, so a near
     * miss collapses one more crumb rather than clipping the current page.
     */
    function fits(nav, children, current) {
        var visible = children.filter(function (el) {
            return el.style.display !== 'none';
        });
        var gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
        var total = gap * (visible.length - 1);
        visible.forEach(function (el) {
            total += el === current ? el.scrollWidth : el.getBoundingClientRect().width;
        });
        return total <= nav.clientWidth - 1;
    }

    /**
     * Collapse / uncollapse a single breadcrumb.
     */
    function updateCollapse(nav) {
        // Hidden (closed sheet, inactive tab) — measure once it has a width
        if (!nav.clientWidth) return;

        var existing = nav.querySelector(':scope > .' + OVERFLOW_CLASS);
        if (existing && existing._menu.classList.contains('show')) {
            nav._collapsePending = true;
            return;
        }

        var overflowSep = existing ? existing._sep : null;
        var children = Array.prototype.slice.call(nav.children).filter(function (el) {
            return el !== existing && el !== overflowSep;
        });
        var crumbs = children.filter(function (el) {
            return !el.classList.contains(SEP_CLASS);
        });

        nav.classList.add(ACTIVE_CLASS);

        // Needs a middle crumb to collapse, and separators to rebuild around it
        var firstSep = crumbs[0] && crumbs[0].nextElementSibling;
        if (crumbs.length < 3 || !firstSep || !firstSep.classList.contains(SEP_CLASS)) return;

        var overflow = getOrCreateOverflow(nav, firstSep);

        // 1. Reset — show every crumb, hide the "…"
        children.forEach(function (el) { setShown(el, true); });
        setShown(overflow, false);
        setShown(overflow._sep, false);

        var current = crumbs[crumbs.length - 1];
        if (fits(nav, children, current)) return;

        // 2. Collapse middle crumbs (each with the separator after it)
        //    left to right until the trail fits
        setShown(overflow, true);
        setShown(overflow._sep, true);

        var menu = overflow._menu;
        menu.innerHTML = '';

        var middle = crumbs.slice(1, -1);
        for (var i = 0; i < middle.length; i++) {
            var crumb = middle[i];
            setShown(crumb, false);
            var sep = crumb.nextElementSibling;
            if (sep && sep.classList.contains(SEP_CLASS)) setShown(sep, false);

            // Clone so data-sheet and other attributes carry over; ids would
            // duplicate the hidden original
            var item = crumb.cloneNode(true);
            item.removeAttribute('style');
            item.removeAttribute('id');
            item.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
            item.classList.add('dropdown-item');
            menu.appendChild(item);

            if (fits(nav, children.concat(overflow, overflow._sep), current)) break;
        }
    }

    var observer = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
            var nav = entry.target;
            var width = entry.contentRect.width;
            // Only width matters; collapsing can change height, which would
            // otherwise re-trigger this observer
            if (lastWidth.get(nav) === width) return;
            lastWidth.set(nav, width);
            updateCollapse(nav);
        });
    });

    // Re-measure every breadcrumb now; the width hasn't changed but its
    // contents (swapped crumbs, a web font swapping in) may have
    function updateAll() {
        document.querySelectorAll(SELECTOR).forEach(function (nav) {
            observer.observe(nav);
            updateCollapse(nav);
        });
    }

    function init() {
        // The observer's first callback does the initial layout
        document.querySelectorAll(SELECTOR).forEach(function (nav) {
            observer.observe(nav);
        });
        if (document.fonts) document.fonts.ready.then(updateAll);
        // HTMX support — new breadcrumbs, or new crumbs in an existing one
        document.body.addEventListener('htmx:afterSettle', updateAll);
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
